import { useState } from "react";
import { useRouter } from "next/router";
import { useAuth, useSignIn } from "@clerk/nextjs";
import type { AttemptSecondFactorParams } from "@clerk/shared/types";
import { type FieldValues, useForm } from "react-hook-form";
import { INVITATION_ACCEPTED_MESSAGE } from "@remi/constants";
import { AuthStrategy } from "@remi/enums/auth-strategy";
import { RnArrowLeft, RnX } from "@remi/icons/next";
import { strings } from "@remi/localization";
import { remiLogger } from "@remi/logging";
import { RemiAlert, RemiButton, RemiCollapsible, RemiCollapsibleContent, RemiInput, useEffect } from "@remi/next-ui";

import { getClerkErrorCode, isClerkInternalError } from "~/utils/clerk";
import {
  factorKey,
  handleResetPasswordResult,
  listSecondFactorOptions,
  pickDefaultSecondFactor,
  type SelectedSecondFactor,
} from "~/utils/mfa";
import { getSafeRedirectTarget } from "~/utils/url";
import { Button } from "~/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "~/components/ui/form";
import { PasswordStrength } from "~/components/ui/passwordStrength";
import { formatMaskedPhoneIdentifier } from "~/components/user/mfa/format-phone";
import { MfaChallengeBackupCode } from "~/components/user/mfa/MfaChallengeBackupCode";
import { MfaChallengePicker } from "~/components/user/mfa/MfaChallengePicker";
import { MfaChallengeSms } from "~/components/user/mfa/MfaChallengeSms";
import { MfaChallengeTotp } from "~/components/user/mfa/MfaChallengeTotp";
import { OtpForm } from "~/components/user/OtpForm";
import { SignInWrapper } from "~/components/user/SignInWrapper";

const FormStage = {
  SignIn: "SignIn",
  ForgotPassword: "ForgotPassword",
  VerifyCode: "VerifyCode",
  ResetPassword: "ResetPassword",
  MfaChallenge: "MfaChallenge",
  MfaChallengePicker: "MfaChallengePicker",
} as const;

type FormStage = keyof typeof FormStage;

// VerifyCode appears in the user's password-reset flow (they clicked the
// emailed link), so "Reset password" is intentional for both stages.
const PASSWORD_STAGE_TITLES = {
  SignIn: "Sign in",
  ForgotPassword: "Forgot password",
  VerifyCode: "Reset password",
  ResetPassword: "Reset password",
} as const;

type PasswordStage = keyof typeof PASSWORD_STAGE_TITLES;

const PASSWORD_STAGE_SUBMIT_TEXT = {
  SignIn: "Sign in",
  ForgotPassword: "Send reset link",
  VerifyCode: "Submit",
  ResetPassword: "Reset password",
} as const satisfies Record<PasswordStage, string>;

const isPasswordStage = (stage: FormStage): stage is PasswordStage =>
  stage === FormStage.SignIn ||
  stage === FormStage.ForgotPassword ||
  stage === FormStage.VerifyCode ||
  stage === FormStage.ResetPassword;

type MfaStage = typeof FormStage.MfaChallenge | typeof FormStage.MfaChallengePicker;

const isMfaStage = (stage: FormStage): stage is MfaStage =>
  stage === FormStage.MfaChallenge || stage === FormStage.MfaChallengePicker;

const SignInPage = () => {
  const [formStage, setFormStage] = useState<FormStage>(FormStage.SignIn);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [otp, setOtp] = useState("");
  const [isPasswordStrongEnough, setIsPasswordStrongEnough] = useState(false);
  const [mfaOptions, setMfaOptions] = useState<SelectedSecondFactor[]>([]);
  const [activeMfaFactor, setActiveMfaFactor] = useState<SelectedSecondFactor | null>(null);
  const [mfaCellError, setMfaCellError] = useState(false);

  const { signIn, setActive } = useSignIn();
  const form = useForm();
  const { isSignedIn, userId } = useAuth();
  const router = useRouter();

  const email = form.watch("identifier");
  const password = form.watch("password");

  const redirectQueryParam = typeof router.query.redirect_url === "string" ? router.query.redirect_url : null;
  const messageQueryParam = typeof router.query.message === "string" ? router.query.message : null;
  const redirectTarget = getSafeRedirectTarget(redirectQueryParam);

  const errorMessages: Record<string, string | undefined> = strings.shared.auth.error_messages;

  useEffect(() => {
    if (!isSignedIn || !userId) {
      return;
    }
    if (redirectTarget.kind === "clerk-oauth") {
      window.location.assign(redirectTarget.url);
      return;
    }
    window.location.assign(redirectTarget.path);
  }, [isSignedIn, userId, redirectTarget]);

  const toggleForm = () => {
    form.resetField("password", undefined);
    form.resetField("code");
    setError("");
    setFormStage(formStage === FormStage.SignIn ? FormStage.ForgotPassword : FormStage.SignIn);
  };

  const resetPassword = async (data: FieldValues) => {
    try {
      if (!signIn) {
        remiLogger.error("signIn is undefined");
        return;
      }

      const res = await signIn.resetPassword({
        password: data.password,
      });

      const result = handleResetPasswordResult(res);
      switch (result.action) {
        case "mfaRequired":
          await enterMfaChallenge(result.factors);
          return;
        case "complete":
          await setActive({ session: result.sessionId });
          return;
        case "error":
          remiLogger.error("Password reset completed with an unusable result.", {
            attributes: { reason: result.reason },
          });
          setError("An error occurred");
          return;
      }
    } catch (error) {
      if (isClerkInternalError(error)) {
        remiLogger.info("Clerk internal error occurred during password reset; reloading.", {
          clerkError: String(error),
        });
        window.location.reload();
        return;
      }
      setError(errorMessages[getClerkErrorCode(error)] ?? "An error occurred");
    }
  };

  const verifyCode = async (data: FieldValues) => {
    try {
      if (!signIn) {
        remiLogger.error("signIn is undefined");
        return;
      }

      await signIn.attemptFirstFactor({
        strategy: AuthStrategy.reset_password_email_code,
        code: data.code,
      });

      setFormStage(FormStage.ResetPassword);
    } catch (error) {
      setError(errorMessages[getClerkErrorCode(error)] ?? "An error occurred");
    }
  };

  const prepareSecondFactorIfNeeded = async (factor: SelectedSecondFactor) => {
    if (!signIn) {
      return;
    }
    if (factor.strategy !== "phone_code") {
      return;
    }
    await signIn.prepareSecondFactor({
      strategy: "phone_code",
      phoneNumberId: factor.phoneNumberId,
    });
    remiLogger.info("MFA second factor was prepared.", { strategy: factor.strategy });
  };

  const enterMfaChallenge = async (factors: SelectedSecondFactor[]) => {
    const defaultFactor = pickDefaultSecondFactor(factors);
    if (!defaultFactor) {
      remiLogger.warn("MFA challenge could not start: no usable second factor.", {
        availableStrategies: factors.map((f) => f.strategy),
      });
      setError("Sign-in needs a second factor but none are available on this account.");
      return;
    }
    setMfaOptions(factors);
    setActiveMfaFactor(defaultFactor);
    setMfaCellError(false);
    remiLogger.info("MFA challenge started.", {
      defaultStrategy: defaultFactor.strategy,
      availableStrategies: factors.map((f) => f.strategy),
    });
    try {
      await prepareSecondFactorIfNeeded(defaultFactor);
    } catch (error) {
      remiLogger.warn("MFA prepare failed.", {
        clerkErrorCode: getClerkErrorCode(error),
        strategy: defaultFactor.strategy,
      });
    }
    setFormStage(FormStage.MfaChallenge);
  };

  const switchMfaFactor = async (factor: SelectedSecondFactor) => {
    const previous = activeMfaFactor?.strategy ?? "none";
    setActiveMfaFactor(factor);
    setMfaCellError(false);
    setError("");
    remiLogger.info("MFA factor was switched.", {
      fromStrategy: previous,
      toStrategy: factor.strategy,
    });
    try {
      await prepareSecondFactorIfNeeded(factor);
    } catch (error) {
      remiLogger.warn("MFA prepare failed on switch.", {
        clerkErrorCode: getClerkErrorCode(error),
        strategy: factor.strategy,
      });
    }
    setFormStage(FormStage.MfaChallenge);
  };

  const resendChallengeSms = async () => {
    if (!activeMfaFactor) {
      return;
    }
    setError("");
    try {
      await prepareSecondFactorIfNeeded(activeMfaFactor);
    } catch (error) {
      remiLogger.warn("MFA SMS resend failed.", { clerkErrorCode: getClerkErrorCode(error) });
      setError(errorMessages[getClerkErrorCode(error)] ?? "Couldn't send a new code. Try again.");
    }
  };

  const buildSecondFactorAttempt = (factor: SelectedSecondFactor, code: string): AttemptSecondFactorParams => {
    switch (factor.strategy) {
      case "totp":
        return { strategy: "totp", code };
      case "phone_code":
        return { strategy: "phone_code", code };
      case "backup_code":
        return { strategy: "backup_code", code };
    }
  };

  const attemptMfa = async (rawCode: string) => {
    try {
      if (!signIn) {
        remiLogger.error("signIn is undefined");
        return;
      }
      if (!activeMfaFactor) {
        remiLogger.error("attemptMfa called without an active factor");
        return;
      }
      const code = rawCode.trim();
      if (!code) {
        return;
      }
      setLoading(true);
      setMfaCellError(false);
      setError("");
      const startedAt = Date.now();
      const res = await signIn.attemptSecondFactor(buildSecondFactorAttempt(activeMfaFactor, code));
      if (res.status === "complete") {
        if (!res.createdSessionId) {
          remiLogger.error("Session was not created after MFA");
          setLoading(false);
          return;
        }
        remiLogger.info("MFA challenge succeeded.", {
          strategy: activeMfaFactor.strategy,
          durationMs: Date.now() - startedAt,
        });
        await setActive({ session: res.createdSessionId });
        // Leave `loading` true so the OTP cells stay disabled while the
        // post-setActive useEffect navigates to the redirect target.
        return;
      }
      remiLogger.warn("MFA challenge returned an incomplete status.", {
        strategy: activeMfaFactor.strategy,
        status: res.status,
      });
      setLoading(false);
    } catch (error) {
      if (isClerkInternalError(error)) {
        remiLogger.warn("Clerk internal error occurred during MFA; reloading.", {
          clerkError: String(error),
        });
        window.location.reload();
        return;
      }
      const errCode = getClerkErrorCode(error);
      const isUserMistake = errCode === "form_code_incorrect";
      const logLevel = isUserMistake ? "info" : "warn";
      remiLogger[logLevel]("MFA challenge was rejected.", {
        strategy: activeMfaFactor?.strategy ?? "unknown",
        errorCode: errCode,
      });
      if (errCode === "form_code_incorrect") {
        setMfaCellError(true);
      }
      setError(errorMessages[errCode] ?? "That code didn't match. Try again.");
      setLoading(false);
    }
  };

  const createSignIn = async (data: FieldValues) => {
    try {
      if (!signIn) {
        remiLogger.error("signIn is undefined");
        return;
      }

      const strategy =
        data.password || formStage === FormStage.SignIn
          ? AuthStrategy.password
          : AuthStrategy.reset_password_email_code;

      const res = await signIn.create({
        identifier: data.identifier,
        password: data.password,
        strategy,
      });

      if (res.status === "needs_second_factor") {
        const factors = listSecondFactorOptions(res.supportedSecondFactors);
        await enterMfaChallenge(factors);
        return;
      }

      if (res.status === "complete") {
        if (!res.createdSessionId) {
          remiLogger.error("Session was not created");
          return;
        }
      }

      if (strategy === AuthStrategy.password) {
        await setActive({ session: res.createdSessionId });
      } else {
        setOtp("");
        setFormStage(FormStage.VerifyCode);
      }
    } catch (error) {
      if (isClerkInternalError(error)) {
        remiLogger.info("Clerk internal error occurred during sign-in; reloading.", {
          clerkError: String(error),
        });
        window.location.reload();
        return;
      }
      const errCode = getClerkErrorCode(error);

      // If user is already logged in, redirect them instead of showing error
      if (errCode === "session_exists") {
        if (redirectTarget.kind === "clerk-oauth") {
          window.location.assign(redirectTarget.url);
          return;
        }
        window.location.assign(redirectTarget.path);
        return;
      }

      setError(errorMessages[errCode] ?? "An error occurred");
    }
  };

  const handleResendCode = async () => {
    setOtp("");
    await createSignIn({ identifier: email });
    setFormStage(FormStage.VerifyCode);
  };

  const restartSignIn = () => {
    setError("");
    setOtp("");
    setMfaOptions([]);
    setActiveMfaFactor(null);
    setMfaCellError(false);
    setLoading(false);
    form.resetField("password", undefined);
    form.resetField("code", undefined);
    setFormStage(FormStage.SignIn);
  };

  const handleBottomButton = () => {
    if (formStage === FormStage.SignIn) {
      toggleForm();
      return;
    }
    if (isMfaStage(formStage)) {
      restartSignIn();
      return;
    }
    toggleForm();
  };

  const bottomButtonText = formStage === FormStage.SignIn ? "Forgot password?" : "Return to sign in";

  const onSubmit = async (data: FieldValues) => {
    setLoading(true);
    if (formStage === FormStage.ResetPassword) {
      await resetPassword(data);
    } else if (formStage === FormStage.VerifyCode) {
      await verifyCode(data);
    } else {
      await createSignIn(data);
    }
    setLoading(false);
  };

  const passwordStage: PasswordStage = isPasswordStage(formStage) ? formStage : FormStage.SignIn;

  // OtpForm auto-submits on the 6th digit in VerifyCode; everything else has
  // an explicit submit button.
  const showSubmitButton = isPasswordStage(formStage) && formStage !== FormStage.VerifyCode;

  const challengeHasOtherMethods = mfaOptions.length > 1;

  return (
    <SignInWrapper title="Sign in - Remi">
      <div className="flex h-full items-center justify-center">
        <div className="flex min-h-[280px] w-full max-w-[336px] flex-col space-y-6">
          <h1 className="sr-only">Sign in to Remi</h1>
          {isPasswordStage(formStage) && (
            <Form {...form}>
              <form className="flex w-full justify-center" onSubmit={form.handleSubmit((data) => void onSubmit(data))}>
                <div className="w-full space-y-6">
                  <h2 className="font-semibold text-lg text-text-secondary">{PASSWORD_STAGE_TITLES[passwordStage]}</h2>
                  <div className="flex-col space-y-4">
                    {formStage !== FormStage.ResetPassword && (
                      <FormField
                        control={form.control}
                        name="identifier"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RemiInput
                                {...field}
                                placeholder="Email"
                                onChange={(event) => {
                                  setError("");
                                  field.onChange(event);
                                }}
                                type="email"
                                size="lg"
                                autoComplete="email"
                                error={!!error && error !== errorMessages.form_code_incorrect}
                                disabled={formStage === FormStage.VerifyCode}
                                className="w-full sm:w-full sm:min-w-0"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    )}
                    {formStage === FormStage.VerifyCode && (
                      <div className="flex-col">
                        <OtpForm
                          otp={otp}
                          handleResend={handleResendCode}
                          setOtp={setOtp}
                          onSubmit={(otp) => {
                            form.setValue("code", otp);
                            void form.handleSubmit((data) => void onSubmit(data))();
                          }}
                          resetError={() => setError("")}
                        />
                      </div>
                    )}
                    {(formStage === FormStage.SignIn || formStage === FormStage.ResetPassword) && (
                      <div>
                        <FormField
                          control={form.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <RemiInput
                                  {...field}
                                  placeholder="Password"
                                  type="password"
                                  className="w-full sm:w-full sm:min-w-0"
                                  size="lg"
                                  autoComplete={
                                    formStage === FormStage.ResetPassword ? "new-password" : "current-password"
                                  }
                                  onChange={(event) => {
                                    setError("");
                                    field.onChange(event);
                                  }}
                                  error={
                                    !!error ||
                                    (formStage === FormStage.ResetPassword && password && !isPasswordStrongEnough)
                                  }
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <RemiCollapsible open={password && formStage === FormStage.ResetPassword}>
                          <RemiCollapsibleContent>
                            <PasswordStrength
                              password={password}
                              email={email}
                              setIsStrongEnough={setIsPasswordStrongEnough}
                              className="mt-3"
                            />
                          </RemiCollapsibleContent>
                        </RemiCollapsible>
                      </div>
                    )}
                    <RemiCollapsible open={!!error}>
                      <RemiCollapsibleContent transitionDuration="none">
                        <RemiAlert
                          variant="destructive"
                          hasButtons={false}
                          icon={<RnX size="sm" />}
                          centerContent={true}
                          className="items-center text-text-destructive"
                        >
                          <div className="font-medium text-xs">{error}</div>
                        </RemiAlert>
                      </RemiCollapsibleContent>
                    </RemiCollapsible>
                    <RemiCollapsible open={!!messageQueryParam && messageQueryParam === INVITATION_ACCEPTED_MESSAGE}>
                      <RemiCollapsibleContent transitionDuration="none">
                        <RemiAlert
                          variant="destructive"
                          hasButtons={false}
                          icon={<RnX size="sm" />}
                          centerContent={true}
                          className="items-center text-text-destructive"
                        >
                          <div className="font-medium text-xs">
                            Invitation already accepted. Please login to continue.
                          </div>
                        </RemiAlert>
                      </RemiCollapsibleContent>
                    </RemiCollapsible>
                  </div>
                  <div>
                    {showSubmitButton && (
                      <Button
                        type="submit"
                        className="w-full"
                        size="large"
                        loading={loading}
                        disabled={
                          !signIn ||
                          loading ||
                          (formStage === FormStage.SignIn && (!password || !email)) ||
                          (formStage === FormStage.ForgotPassword && !email) ||
                          (formStage === FormStage.ResetPassword && !isPasswordStrongEnough)
                        }
                      >
                        {PASSWORD_STAGE_SUBMIT_TEXT[passwordStage]}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="none"
                      size="large"
                      className="mt-1 w-full text-text-secondary hover:text-text-primary"
                      onClick={handleBottomButton}
                    >
                      {bottomButtonText}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          )}

          {formStage === FormStage.MfaChallenge && activeMfaFactor && (
            <div className="flex w-full flex-col gap-6 pl-2">
              {activeMfaFactor.strategy === "totp" && (
                <MfaChallengeTotp
                  key={factorKey(activeMfaFactor)}
                  onVerify={(code) => void attemptMfa(code)}
                  onTryAnotherMethod={
                    challengeHasOtherMethods ? () => setFormStage(FormStage.MfaChallengePicker) : undefined
                  }
                  resetError={() => {
                    setMfaCellError(false);
                    setError("");
                  }}
                  loading={loading}
                  error={mfaCellError}
                  errorMessage={mfaCellError ? error : undefined}
                />
              )}
              {activeMfaFactor.strategy === "phone_code" && (
                <MfaChallengeSms
                  key={factorKey(activeMfaFactor)}
                  maskedIdentifier={formatMaskedPhoneIdentifier(activeMfaFactor.safeIdentifier)}
                  onVerify={(code) => void attemptMfa(code)}
                  onResend={() => void resendChallengeSms()}
                  onTryAnotherMethod={
                    challengeHasOtherMethods ? () => setFormStage(FormStage.MfaChallengePicker) : undefined
                  }
                  resetError={() => {
                    setMfaCellError(false);
                    setError("");
                  }}
                  loading={loading}
                  error={mfaCellError}
                  errorMessage={mfaCellError ? error : undefined}
                />
              )}
              {activeMfaFactor.strategy === "backup_code" && (
                <MfaChallengeBackupCode
                  key={factorKey(activeMfaFactor)}
                  onVerify={(code) => void attemptMfa(code)}
                  onTryAnotherMethod={
                    challengeHasOtherMethods ? () => setFormStage(FormStage.MfaChallengePicker) : undefined
                  }
                  resetError={() => {
                    setMfaCellError(false);
                    setError("");
                  }}
                  loading={loading}
                  error={mfaCellError}
                  errorMessage={mfaCellError ? error : undefined}
                />
              )}
              <RemiCollapsible open={!!error && !mfaCellError}>
                <RemiCollapsibleContent transitionDuration="none">
                  <RemiAlert
                    variant="destructive"
                    hasButtons={false}
                    icon={<RnX size="sm" />}
                    centerContent={true}
                    className="items-center text-text-destructive"
                  >
                    <div className="font-medium text-xs">{error}</div>
                  </RemiAlert>
                </RemiCollapsibleContent>
              </RemiCollapsible>
            </div>
          )}

          {formStage === FormStage.MfaChallengePicker && (
            <div className="flex w-full flex-col gap-6 pl-2">
              <MfaChallengePicker options={mfaOptions} onSelect={(factor) => void switchMfaFactor(factor)} />
            </div>
          )}
        </div>
      </div>
      {isMfaStage(formStage) && (
        <div className="absolute top-[70%] right-0 left-0 flex justify-center">
          <RemiButton
            variant="tertiary"
            size="md"
            icon={<RnArrowLeft size="sm" aria-hidden="true" />}
            onClick={handleBottomButton}
            className="text-text-secondary"
            data-testid="mfa-back-to-sign-in"
          >
            Return to sign in
          </RemiButton>
        </div>
      )}
    </SignInWrapper>
  );
};

export const getServerSideProps = () => ({ props: {} });

export default SignInPage;
